import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiArrowDown, FiArrowLeft, FiExternalLink, FiSend, FiX } from 'react-icons/fi';
import { GoPaperclip } from 'react-icons/go';
import { IoDocumentTextOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';

import { backupLink, imageVariantLink, mapMediaVariants } from '@/common/media';
import { Avatar, type ChatAttachment, ChatMessage, type ChatMessageData } from '@/components';
import { AddToGalleryModal } from '@/components/AddToGalleryModal/AddToGalleryModal';
import { DocumentPreviewModal } from '@/components/DocumentPreview/DocumentPreview';
import { useAuthorizedFileUrl } from '@/hooks/useAuthorizedFileUrl';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { usePersonsMap } from '@/hooks/usePersonsMap';
import { useAuth } from '@/store/auth/auth.store';
import { useMessages } from '@/store/messages/messages.store';
import { useWebSocket } from '@/store/websocket/websocket.store';
import { admitAttachments } from '@/utils/chat-attachments';
import { getChatImageFiles } from '@/utils/chatGalleryFiles';
import { toast } from '@/utils/toast';

import styles from './CrewChat.module.scss';

const PAGE_SIZE = 100;
const NEAR_BOTTOM_THRESHOLD = 150;

/** A photo in a message is at most 240 px tall in a column about 320 wide. */
const CREW_CHAT_IMAGE_WIDTH = 320;

const isImageLink = (url?: string, name?: string) =>
  !!(url || name)?.match(/\.(png|jpe?g|gif|webp|bmp|svg)$/i);

const CrewChatAttachment = ({
  attachment,
  onOpen,
}: {
  attachment: ChatAttachment;
  onOpen: (attachment: ChatAttachment) => void;
}) => {
  const rawLink = attachment.preview ?? attachment.link ?? attachment.url ?? '';
  const { url: src } = useAuthorizedFileUrl(rawLink);
  const type = attachment.type ?? '';
  const isImage = attachment.isImage || type === 'image' || type.startsWith('image/');
  // TIFF outside Safari, a corrupted file kept as uploaded: shown as a file instead.
  const [broken, setBroken] = useState(false);

  if (isImage && src && !broken) {
    return (
      <button
        type="button"
        className={styles.messageImageButton}
        onClick={() => onOpen(attachment)}
      >
        <img
          className={styles.messageImage}
          src={src}
          alt={attachment.name}
          onError={() => setBroken(true)}
        />
      </button>
    );
  }

  if (!src) {
    return (
      <span className={styles.messageFile}>
        <IoDocumentTextOutline />
        <span>{attachment.name}</span>
      </span>
    );
  }

  return (
    <button type="button" className={styles.messageFile} onClick={() => onOpen(attachment)}>
      <IoDocumentTextOutline />
      <span>{attachment.name}</span>
    </button>
  );
};

type CrewChatProps = {
  roomId: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  role?: string;
  title?: string;
  chatType?: 'one_to_one' | 'group';
  onBack: () => void;
};

const CrewChat = ({
  roomId,
  firstName,
  lastName,
  avatar,
  role,
  title,
  chatType = 'one_to_one',
  onBack,
}: CrewChatProps) => {
  const navigate = useNavigate();
  const personsMap = usePersonsMap();
  const currentUserId = useAuth((state) => state.session?.id);
  const getMessages = useMessages((state) => state.getMessages);
  const sendMessage = useMessages((state) => state.sendMessage);
  const uploadFilesSeparately = useMessages((state) => state.uploadFilesSeparately);
  const messagesByRoom = useMessages((state) => state.messagesByRoom);
  const subscribe = useWebSocket((state) => state.subscribe);
  const wsSendMessage = useWebSocket((state) => state.sendMessage);
  const wsEditMessage = useWebSocket((state) => state.editMessage);
  const wsDeleteMessage = useWebSocket((state) => state.deleteMessage);
  const subscribedRoomId = useWebSocket((state) => state.currentRoomId);
  const unsubscribe = useWebSocket((state) => state.unsubscribe);
  const connectionStatus = useWebSocket((state) => state.connectionStatus);
  const wsMessages = useWebSocket((state) => state.messages);
  const pendingMessages = useWebSocket((state) => state.pendingMessages);

  const [galleryFiles, setGalleryFiles] = useState<File[] | null>(null);
  const [previewAttachment, setPreviewAttachment] = useState<ChatAttachment | null>(null);

  const openGalleryModal = async (message: ChatMessageData) => {
    const files = await getChatImageFiles(message);
    if (files.length === 0) {
      toast.error('Couldn’t open the photo. Please try again shortly.');
      return;
    }
    setGalleryFiles(files);
  };
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const bodyRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const lastCountRef = useRef(0);

  // The store resumes the room's stream from the cursor its last step left.
  const fetchMore = useCallback(
    async () => getMessages(roomId, PAGE_SIZE, true),
    [getMessages, roomId],
  );

  const {
    isLoading: isLoadingMore,
    reset: resetInfiniteScroll,
    setInitialOffset,
    setHasMore,
  } = useInfiniteScroll({
    containerRef: bodyRef,
    fetchMore,
    pageSize: PAGE_SIZE,
    threshold: 200,
    enabled: !isLoading,
    direction: 'up',
  });

  useEffect(() => {
    setIsLoading(true);
    resetInfiniteScroll();
    lastCountRef.current = 0;

    getMessages(roomId, PAGE_SIZE, false)
      .then((loaded) => {
        setInitialOffset(PAGE_SIZE);
        if (!loaded?.hasMore) {
          setHasMore(false);
        }
      })
      .finally(() => setIsLoading(false));
  }, [roomId, getMessages, resetInfiniteScroll, setHasMore, setInitialOffset]);

  useEffect(() => {
    if (connectionStatus !== 'connected') {
      return;
    }
    subscribe(roomId);
  }, [roomId, connectionStatus, subscribe]);

  useEffect(() => () => unsubscribe(), [unsubscribe]);

  const senderName = useCallback(
    (creatorId: string) => {
      const counterpart = `${firstName ?? ''} ${lastName ?? ''}`.trim();
      if (counterpart) return counterpart;

      const info = personsMap.get(creatorId)?.person_info;
      return `${info?.first_name ?? ''} ${info?.last_name ?? ''}`.trim() || 'Unknown';
    },
    [firstName, lastName, personsMap],
  );

  const messages = useMemo(() => {
    const fromHttp: ChatMessageData[] = (messagesByRoom[roomId] ?? []).map((msg) => ({
      id: msg.id,
      sender: msg.creator_id === currentUserId ? 'You' : senderName(msg.creator_id),
      text: msg.text,
      messageType: msg.type,
      fromSelf: msg.creator_id === currentUserId,
      isRead: msg.read,
      isEdited: new Date(msg.updated_at).getTime() - new Date(msg.created_at).getTime() > 1000,
      timestamp: new Date(msg.created_at).getTime(),
      creatorId: msg.creator_id,
      attachments: msg.media_files.map((file) => ({
        id: file.id,
        name: file.file_name || file.name,
        url: file.link,
        link: file.link,
        isImage: isImageLink(file.link, file.file_name || file.name),
        type: file.type,
        size: file.size,
        backup: backupLink(mapMediaVariants(file.link, file.variants)),
        preview: imageVariantLink(file, CREW_CHAT_IMAGE_WIDTH, 0),
      })),
    }));

    const fromWs: ChatMessageData[] = (wsMessages[roomId] ?? []).map((msg) => ({
      id: msg.id,
      sender: msg.creator_id === currentUserId ? 'You' : senderName(msg.creator_id),
      text: msg.text,
      fromSelf: msg.creator_id === currentUserId,
      isEdited: !!(
        msg.updated_at &&
        new Date(msg.updated_at).getTime() - new Date(msg.created_at).getTime() > 1000
      ),
      timestamp: new Date(msg.created_at).getTime(),
      creatorId: msg.creator_id,
      attachments: msg.media_files.map((file) => ({
        id: file.id,
        name: file.name || file.file_name,
        url: file.url,
        link: file.url,
        isImage: file.type === 'image' || isImageLink(file.url, file.name || file.file_name),
        type: file.type,
        size: file.size,
        backup: backupLink(mapMediaVariants(file.url, file.variants)),
        preview: imageVariantLink(
          { link: file.url, variants: file.variants },
          CREW_CHAT_IMAGE_WIDTH,
          0,
        ),
      })),
    }));

    const fromPending: ChatMessageData[] = pendingMessages
      .filter((msg) => msg.roomId === roomId)
      .map((msg) => ({
        id: msg.tid,
        sender: 'You',
        text: msg.text,
        fromSelf: true,
        messageType: msg.status === 'sending' ? 'pending' : 'failed',
        timestamp: new Date(msg.createdAt).getTime(),
        attachments: (msg.attachments ?? []).map((attachment) => ({
          id: attachment.id,
          name: attachment.name,
          url: attachment.localUrl,
          link: attachment.localUrl,
          isImage: attachment.isImage,
          type: attachment.type,
        })),
      }));

    const unique = new Map<string, ChatMessageData>();
    [...fromWs, ...fromHttp, ...fromPending].forEach((msg) => unique.set(msg.id, msg));

    return [...unique.values()].sort((a, b) => a.timestamp - b.timestamp);
  }, [currentUserId, senderName, messagesByRoom, pendingMessages, roomId, wsMessages]);

  const scrollToBottom = useCallback((smooth = true) => {
    const body = bodyRef.current;
    if (!body) {
      return;
    }
    body.scrollTo({ top: body.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  const isNearBottom = useCallback(() => {
    const body = bodyRef.current;
    if (!body) {
      return true;
    }
    return body.scrollHeight - body.scrollTop - body.clientHeight < NEAR_BOTTOM_THRESHOLD;
  }, []);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const isFirstRender = lastCountRef.current === 0;
    const hasNewMessages = messages.length > lastCountRef.current;
    lastCountRef.current = messages.length;

    if (isFirstRender || (hasNewMessages && isNearBottom())) {
      requestAnimationFrame(() => scrollToBottom(!isFirstRender));
    }
  }, [isLoading, isNearBottom, messages.length, scrollToBottom]);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) {
      return;
    }

    const onScroll = () => setShowScrollButton(!isNearBottom());
    body.addEventListener('scroll', onScroll);

    return () => body.removeEventListener('scroll', onScroll);
  }, [isNearBottom]);

  const handleSend = async () => {
    const trimmed = text.trim();
    if (isSending || (!trimmed && files.length === 0)) {
      return;
    }

    setIsSending(true);
    try {
      if (files.length > 0) {
        await uploadFilesSeparately(roomId, files, trimmed);
      } else if (connectionStatus === 'connected' && subscribedRoomId === roomId) {
        wsSendMessage(trimmed);
      } else {
        await sendMessage(roomId, trimmed);
      }
      setText('');
      setFiles([]);
      scrollToBottom();
    } finally {
      setIsSending(false);
    }
  };

  const name = title ?? (`${firstName ?? ''} ${lastName ?? ''}`.trim() || 'Chat');

  return (
    <article className={styles.chat}>
      <button type="button" className={styles.backBtn} onClick={onBack}>
        <FiArrowLeft /> Back
      </button>

      <div className={styles.head}>
        <Avatar firstName={firstName} lastName={lastName} src={avatar} size={44} />
        <div className={styles.headText}>
          <span className={styles.name}>{name}</span>
          {role && <span className={styles.role}>{role}</span>}
        </div>
        <button
          type="button"
          className={styles.openChatBtn}
          onClick={() => navigate(`/inbox/${roomId}`)}
        >
          Open chat <FiExternalLink size={14} />
        </button>
      </div>

      <div className={styles.body} ref={bodyRef}>
        {isLoadingMore && <span className={styles.loadingMore}>Loading…</span>}
        {isLoading ? (
          <span className={styles.state}>Loading messages…</span>
        ) : messages.length === 0 ? (
          <span className={styles.state}>No messages yet</span>
        ) : (
          messages.map((message, index) => {
            const previous = messages[index - 1];
            const showDateLabel =
              !previous ||
              new Date(previous.timestamp).toDateString() !==
                new Date(message.timestamp).toDateString();

            return (
              <ChatMessage
                key={message.id}
                message={message}
                avatarSrc={message.fromSelf ? undefined : avatar}
                showDateLabel={showDateLabel}
                renderAttachment={(attachment) => (
                  <CrewChatAttachment
                    key={attachment.id}
                    attachment={attachment}
                    onOpen={setPreviewAttachment}
                  />
                )}
                onAddToGallery={(msg) => void openGalleryModal(msg)}
                onEdit={(id, newText) => wsEditMessage(id, newText)}
                onDelete={(id) => wsDeleteMessage(id)}
                chatType={chatType}
              />
            );
          })
        )}
        {showScrollButton && (
          <button
            type="button"
            className={styles.scrollBtn}
            onClick={() => scrollToBottom()}
            aria-label="Scroll to Bottom"
          >
            <FiArrowDown />
          </button>
        )}
      </div>

      {files.length > 0 && (
        <div className={styles.attachments}>
          {files.map((file, index) => (
            <span className={styles.attachmentChip} key={`${file.name}-${index}`}>
              <IoDocumentTextOutline />
              <span>{file.name}</span>
              <button
                type="button"
                className={styles.removeChip}
                aria-label={`Remove ${file.name}`}
                onClick={() => setFiles((current) => current.filter((_, i) => i !== index))}
              >
                <FiX size={14} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className={styles.composer}>
        <input
          className={styles.fileInput}
          ref={fileInputRef}
          type="file"
          multiple
          onChange={(event) => {
            const selected = Array.from(event.target.files ?? []);
            event.target.value = '';
            // All the files go out as one message, held to the same limits as the chat composer.
            const { accepted, refusals } = admitAttachments(files, selected);
            refusals.forEach((reason) => toast.error(reason));
            setFiles((current) => [...current, ...accepted]);
          }}
        />
        <button
          type="button"
          className={styles.iconBtn}
          aria-label="Attach File"
          disabled={isSending}
          onClick={() => fileInputRef.current?.click()}
        >
          <GoPaperclip />
        </button>
        <input
          className={styles.input}
          placeholder="Write a message"
          value={text}
          disabled={isSending}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void handleSend();
            }
          }}
        />
        <button
          type="button"
          className={`${styles.iconBtn} ${styles.sendBtn}`}
          aria-label="Send Message"
          disabled={isSending || (!text.trim() && files.length === 0)}
          onClick={() => void handleSend()}
        >
          <FiSend />
        </button>
      </div>

      <AddToGalleryModal
        open={!!galleryFiles}
        files={galleryFiles ?? undefined}
        onClose={() => setGalleryFiles(null)}
      />

      {previewAttachment && (
        <DocumentPreviewModal
          document={{
            name: previewAttachment.name,
            contentType: previewAttachment.type ?? 'application/octet-stream',
            storageLink: previewAttachment.link ?? previewAttachment.url ?? '',
            backupLink: previewAttachment.backup,
          }}
          onClose={() => setPreviewAttachment(null)}
        />
      )}
    </article>
  );
};

export default CrewChat;
